import type {
  OrderTotals,
  OrderTotalsDetailItemId,
  UserInOrderDetail,
} from 'shared/domain';
import type {
  OrderDetailRawResp,
  OrderDetailTotalDetailsRaw,
} from 'shared/infra';

export function generateBasicName(user: OrderDetailRawResp['user']): string {
  const firstName = user.first_name ?? '';
  const lastName = user.last_name ?? '';
  const firstWord = firstName.trim().split(/\s+/)[0] || '';
  if (!firstWord && lastName.trim()) {
    return lastName.trim().split(/\s+/)[0] || '';
  }
  const lastNameFirstLetter = lastName.trim().charAt(0);
  return lastNameFirstLetter
    ? `${firstWord} ${lastNameFirstLetter}.`
    : firstWord;
}

export function buildUserAddress(
  user: OrderDetailRawResp['user'],
): UserInOrderDetail['address'] {
  const street = user.address === ' --- ' ? '' : user.address ?? '';
  const _description = user.address_description ?? '';
  const description = _description === ' --- ' ? '' : _description;
  const zipCode = user.zip_code ?? '';
  const neighborhood = user.neighborhood ?? '';
  const _parts = [street, description, zipCode, neighborhood];
  const full = _parts.filter(part => part.trim() !== '').join(', ');
  return {
    street,
    description,
    location: {
      zipCode,
      neighborhood,
      coordinates: {
        lat: user.address_lat ?? NaN,
        lng: user.address_lng ?? NaN,
      },
    },
    full,
  };
}

export function sanitizePhoneNumber(v: UserInOrderDetail['phone']['number']) {
  if (!v) return '';
  const trimmedPhone = v.trim();
  if (trimmedPhone === '') return '';
  if (/^-+$/.test(trimmedPhone)) return '';
  if (/^\*+$/.test(trimmedPhone)) return '';
  return v;
}

export function transformOrderTotalsDetails(
  details: OrderDetailTotalDetailsRaw = [],
): OrderTotals['detail'] {
  const result: OrderTotals['detail'] = {
    length: details.length,
    item: {},
    extraItems: [],
  };
  details.forEach(detail => {
    const baseItem = {
      id: detail.id as OrderTotalsDetailItemId,
      label: detail.text ?? '',
      price: detail.value ?? 0.0,
      isZero: detail.value === 0,
      isNegative: detail.value < 0,
    };
    switch (baseItem.id) {
      case 'subtotal':
        result.item.subtotal = baseItem;
        break;
      case 'discounts_partner_store':
        result.item.discountPartnerStore = baseItem;
        break;
      case 'subtotal_rest':
        result.item.totalStore = baseItem;
        break;
      case 'discounts_rappi':
        result.item.discountRappi = baseItem;
        break;
      case 'suggested_total':
        result.item.suggestedTotal = baseItem;
        break;
      case 'total_discount':
        result.item.totalDiscount = baseItem;
        break;
      default:
        result.extraItems.push(baseItem);
        break;
    }
  });
  return result;
}
